import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, BadgeCheck, AlertTriangle, Award, Star,
  ThumbsUp, AlertCircle, Ban, TrendingDown, Minus,
} from "lucide-react";

// Kuratierte Lucide-Icons zur Auswahl im Klassen-Editor. Der Key entspricht
// dem Wert, der im Backend gespeichert wird.
export const QUALITAETSKLASSE_ICONS = {
  ShieldCheck, BadgeCheck, AlertTriangle, Award, Star,
  ThumbsUp, AlertCircle, Ban, TrendingDown, Minus,
};

// Semantische Farb-Tokens (nutzen die im Theme definierten CSS-Variablen)
export const QUALITAETSKLASSE_FARBEN = {
  success: {
    label: "Grün",
    cls: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]",
    swatch: "hsl(var(--success))",
  },
  info: {
    label: "Blau",
    cls: "bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] hover:bg-[hsl(var(--info))]",
    swatch: "hsl(var(--info))",
  },
  warning: {
    label: "Amber",
    cls: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]",
    swatch: "hsl(var(--warning))",
  },
  destructive: {
    label: "Rot",
    cls: "bg-destructive text-destructive-foreground hover:bg-destructive",
    swatch: "hsl(var(--destructive))",
  },
  primary: {
    label: "Primär",
    cls: "bg-primary text-primary-foreground hover:bg-primary",
    swatch: "hsl(var(--primary))",
  },
  muted: {
    label: "Grau",
    cls: "bg-muted text-muted-foreground hover:bg-muted",
    swatch: "hsl(var(--muted))",
  },
};

// Legacy-Fallback für alte Abrechnungen ohne icon/farbe-Metadaten und für
// den "keine Klasse ermittelbar"-Fall. Wird nur bei fehlenden Metadaten
// verwendet, damit historische Daten weiterhin sinnvoll dargestellt werden.
const LEGACY_FALLBACK = {
  S: { icon: "ShieldCheck", farbe: "success", title: "Spitzenqualität" },
  "1": { icon: "BadgeCheck", farbe: "info", title: "Klasse 1" },
  "2": { icon: "AlertTriangle", farbe: "warning", title: "Klasse 2" },
};

/**
 * Rendert das Klassen-Badge einer Abrechnung/Probe.
 *
 * Props:
 *   klasse    Name der Klasse (z. B. "S", "1", "2", eigene wie "Bio-Top")
 *   icon      (optional) Lucide-Icon-Name aus QUALITAETSKLASSE_ICONS
 *   farbe     (optional) Farb-Token aus QUALITAETSKLASSE_FARBEN
 *   showText  Beschreibung statt Kürzel anzeigen
 *   title     Tooltip-Text (Beschreibung der Klasse)
 */
export const QualityBadge = ({ klasse, icon, farbe, showText = false, title }) => {
  if (!klasse || klasse === "-") {
    return (
      <Badge variant="outline" className="gap-1 text-muted-foreground" title="Keine Klasse ermittelbar">
        <Minus className="h-3 w-3" />
        {showText ? "keine Klasse" : "–"}
      </Badge>
    );
  }
  const fallback = LEGACY_FALLBACK[klasse];
  const finalIcon = icon || fallback?.icon || "BadgeCheck";
  const finalFarbe = farbe || fallback?.farbe || "muted";
  const finalTitle = title || fallback?.title || klasse;

  const Icon = QUALITAETSKLASSE_ICONS[finalIcon] || BadgeCheck;
  const farbCfg = QUALITAETSKLASSE_FARBEN[finalFarbe] || QUALITAETSKLASSE_FARBEN.muted;

  return (
    <Badge data-testid="quality-class-badge"
      className={`${farbCfg.cls} gap-1 font-semibold`}
      title={finalTitle}>
      <Icon className="h-3 w-3" />
      {showText ? finalTitle : klasse}
    </Badge>
  );
};
